import React, { useEffect, useState } from "react";
import { Col, Form, Modal, Row, Table } from "react-bootstrap";
import Select from "react-select";
import { read, utils } from "xlsx";
import { ImportIcon } from "../icons";
import Cookies from "universal-cookie";
import { fetchActionsUtil, useCookie } from "../../utils";
import { URLS } from "../urls";
import { toast } from "react-toastify";
import ModalLoader from "../ModalLoader";
import moment from "moment";

export const ImportEmployees = ({ show, handleClose, refresh }) => {
  const cookies = new Cookies();
  const company_id = cookies.get("company_id");
  const branch_id = useCookie("branch_id");
  const token = "dummy token";

  const [isLoading, setIsLoading] = useState(false);
  const [state, setState] = useState({
    grades: [],
    department: [],
    category: [],
  });

  // const [gradeId, setGradeId] = useState("");
  // const [departmentId, setDepartmentId] = useState("");
  // const [categoryId, setCategoryId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [employeeErrors, setEmployeeErrors] = useState([]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const token = "dummy token";

      const gradesData = fetchActionsUtil(
        `${URLS.backendJobGrades}/get/${branch_id}`,
        "GET",
        token
      );

      const departmentsData = fetchActionsUtil(
        `${URLS.backendDepartments}/get/${branch_id}`,
        "GET",
        token
      );

      const categoryData = fetchActionsUtil(
        `${URLS.backendJobCategory}/get/${branch_id}`,
        "GET",
        token
      );

      const [gradesResult, departmentsResult, categoryResult] =
        await Promise.all([gradesData, departmentsData, categoryData]);

      const grades = gradesResult?.jobGrades?.map((el) => ({
        value: el?._id,
        label: el?.name,
      }));
      const department = departmentsResult?.departments?.map((el) => ({
        value: el?._id,
        label: el?.name,
      }));

      const category = categoryResult?.jobCategories?.map((el) => ({
        value: el?._id,
        label: el?.name,
      }));

      setState({
        grades,
        department,
        category,
      });
    } catch (error) {
      console.log(error);
      toast.error("Fetching Error !!!");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      fetchData();
    }

    return () => {
      isMounted = false;
    };
  }, []);

  const addNewEmployees = ({ rows, deptMap, gradeMap, categoryMap }) => {
    return rows
      ?.map((el) => {
        if (!el) return null;

        // Create new object with keys without spaces (faster than forEach)
        const newObject = {};
        for (const key in el) {
          if (el[key] != null && el[key] !== "") {
            newObject[key.replace(/\s+/g, "")] = el[key];
          }
        }

        // Process supervisors (optimized string operations)
        const supervisorStr = `${newObject?.supervisor_employeeID}` || "";
        const supers = supervisorStr
          ? supervisorStr?.split(",")?.filter(Boolean)
          : [];

        // Generate random username suffix (pre-calculated min/max)
        const random = (Math.random() * 3 + 2) | 0; // Faster than Math.floor
        const emailPrefix = newObject?.jobInfo_email?.split("@")[0];
        const username = emailPrefix ? `${emailPrefix}${random}` : "";

        // Fast lookups using precomputed maps
        const department = newObject.department?.trim()
          ? deptMap.get(newObject.department)
          : null;
        const departmentName = department?.label || "";
        const departmentId = department?.value || null;
        const gradeId = newObject.job_grade
          ? gradeMap.get(newObject.job_grade)
          : null;
        const categoryId = newObject.category
          ? categoryMap.get(newObject.category)
          : null;

        // Optimized date formatting (cache moment format)
        const formatDate = (dateStr) => {
          if (!dateStr) return null;
          return moment(dateStr, "yyyy-MM-dd")
            .add(1, "hours")
            .format("DD/MM/YYYY");
        };

        // Helper for nested objects with null checks
        const createNested = (prefix, fields) => {
          const obj = {};
          for (const field of fields) {
            const val = newObject[`${prefix}_${field}`];
            if (val != null && val !== "") obj[field] = val || "";
          }
          return Object.keys(obj).length ? obj : "";
        };

        // Main employee object construction
        const employee = {
          branch: branch_id,
          supervisor: supers.length ? supers : [],
          department: departmentName,
          isLineManager: newObject.isLineManager === "1",
          personalInfo: {
            name: {
              first: newObject.personalInfo_name_first,
              last: newObject.personalInfo_name_last,
              middle: newObject.personalInfo_name_middle,
            },
            address: createNested("personalInfo_address", [
              "apartment",
              "street",
              "city",
              "state",
              "zip",
            ]),
            phone: createNested("personalInfo_phone", ["home", "alternate"]),
            email: newObject.personalInfo_email,
            birth: formatDate(newObject.personalInfo_birth),
            isAdmin: newObject.admin === "1",
            isSuperAdmin: newObject.admin === "1",
            grantAccess: true,
            govtId: newObject.personalInfo_govtId,
            nationality: newObject.nationality,
            BVN: newObject.BVN,
            taxID: newObject.taxID,
            gender:
              newObject.personalInfo_gender?.charAt(0) === "M"
                ? "Male"
                : newObject.personalInfo_gender?.charAt(0) === "F"
                ? "Female"
                : "",
            martial: newObject.personalInfo_martial,
            govtType: newObject.personalInfo_govtType,
            spouse: createNested("personalInfo_spouse", [
              "name",
              "employer",
              "phone",
            ]),
            prevEmployee: createNested("personalInfo_prevEmployee", [
              "employee",
              "phone",
            ]),
          },
          jobInfo: {
            title: newObject.jobInfo_title,
            category: categoryId,
            employeeId: newObject.jobInfo_employeeId,
            changeableId: newObject.jobInfo_employeeId,
            grade: gradeId,
            company_id: company_id || "",
            supervisor: [],
            department: departmentId,
            workLocation: null,
            email: newObject.jobInfo_email,
            workPhone: newObject.jobInfo_workPhone,
            cellPhone: newObject.jobInfo_cellPhone,
            startDate: formatDate(newObject.jobInfo_startDate),
            joinDate: formatDate(newObject.jobInfo_confirmDate),
            salary: newObject.jobInfo_salary,
            status: newObject.jobInfo_confirmDate ? "Confirmed" : "",
            salaryBasis: "",
            contract: false,
            contractType: "",
            sfa_id: newObject.jobInfo_sfa_id,
            workLocationBranch: null,
            confirmDate: formatDate(newObject.jobInfo_confirmDate),
            state: newObject.jobInfo_state,
          },
          emergencyInfo: {
            name: createNested("emergencyInfo_name", [
              "first",
              "last",
              "middle",
            ]),
            address: createNested("emergencyInfo_address", [
              "apartment",
              "street",
              "city",
              "state",
              "zip",
            ]),
            phone: createNested("emergencyInfo_phone", [
              "primary",
              "secondary",
            ]),
            relation: newObject.emergencyInfo_relation,
            guarantor: createNested("emergencyInfo_guarantor", [
              "title",
              "name",
              "phone",
              "address",
            ]),
            referee: createNested("emergencyInfo_referee", [
              "title",
              "name",
              "phone",
              "address",
            ]),
            employeeHMO: null,
            lifeInsurance: null,
          },
          user: {
            name: {
              first: newObject.personalInfo_name_first,
              last: newObject.personalInfo_name_last,
              middle: newObject.personalInfo_name_middle,
            },
            username,
            email: newObject.jobInfo_email || "",
            password: `${newObject.jobInfo_password}` || "",
            isAdmin: newObject.admin === "1",
            isSuperAdmin: newObject.admin === "1",
            company_id,
            department_id: departmentId,
            grantAccess: newObject.admin === "1",
            branch: branch_id,
            workEmail: newObject.jobInfo_email,
            gender:
              newObject.personalInfo_gender?.charAt(0) === "M"
                ? "Male"
                : newObject.personalInfo_gender?.charAt(0) === "F"
                ? "Female"
                : "",
            Staff_ID: newObject.jobInfo_employeeId,
          },
          bankDetails: createNested("bankDetails", [
            "bankName",
            "accountNumber",
            "accountType",
            "accountOfficer",
            "bankCode",
            "pensionNumber",
            "pensionAdmin",
          ]),
          educationInfo: [
            {
              institution: "",
              subject: "",
              startDate: "",
              completeDate: "",
              degree: "",
              grade: "",
              certificate: "",
            },
          ],
        };

        return employee;
      })
      .filter(Boolean);
  };

  const validateEmployeeFields = (employees) => {
    if (!employees?.length) return [];

    const requiredFields = [
      { path: "user.email", error: "Please enter the personal email" },
      { path: "user.password", error: "Please enter the password" },
      { path: "jobInfo.employeeId", error: "Please enter the employee Id" },
      { path: "jobInfo.email", error: "Please enter the job email" },
      {
        path: "jobInfo.department",
        error:
          "Department not found in excel OR It is missing from HR database",
      },
      {
        path: "jobInfo.grade",
        error: "Job grade not found in excel OR It is missing from HR database",
      },
      { path: "jobInfo.email", error: "Please enter the work email" },
    ];

    return employees.reduce((errors, employee) => {
      const { first, last } = employee.personalInfo?.name || {};
      const employeeName = `${first || ""} ${last || ""}`.trim();
      const employeeId = employee.jobInfo?.employeeId;

      const missing = requiredFields.reduce((acc, field) => {
        const value = field.path
          .split(".")
          .reduce((obj, key) => obj?.[key], employee);
        if (!value) {
          acc.push({
            field: field.path,
            employeeName,
            employeeId,
            error: field.error,
          });
        }
        return acc;
      }, []);

      if (missing.length) {
        errors.push(...missing);
      }

      return errors;
    }, []);
  };

  const updateEmployees = ({ rows, deptMap, gradeMap, categoryMap }) => {
    const formatDate = (dateStr) =>
      dateStr
        ? moment(dateStr, "yyyy-MM-dd").add(1, "hours").format("DD/MM/YYYY")
        : null;

    const createNested = (prefix, fields, source) => {
      const obj = {};
      for (const field of fields) {
        const val = source[`${prefix}_${field}`];
        if (val != null && val !== "") obj[field] = val;
      }
      return Object.keys(obj).length ? obj : null;
    };

    const deepClean = (obj) => {
      if (Array.isArray(obj)) {
        const cleaned = obj
          .map(deepClean)
          .filter(
            (item) =>
              !(
                item == null ||
                (typeof item === "object" && !Object.keys(item).length)
              )
          );
        return cleaned.length ? cleaned : null;
      } else if (obj && typeof obj === "object") {
        for (const key in obj) {
          obj[key] = deepClean(obj[key]);
          if (
            obj[key] == null ||
            (typeof obj[key] === "object" && !Object.keys(obj[key]).length)
          ) {
            delete obj[key];
          }
        }
        return Object.keys(obj).length ? obj : null;
      }
      return obj;
    };

    return rows
      ?.map((el) => {
        if (!el) return null;

        const newObject = {};
        for (const key in el) {
          const val = el[key];
          if (val != null && val !== "") {
            newObject[key.replace(/\s+/g, "")] = val;
          }
        }

        const supervisorStr = `${newObject?.supervisor_employeeID || ""}`;
        const supers = supervisorStr.split(",").filter(Boolean);

        const department = newObject.department?.trim()
          ? deptMap.get(newObject.department)
          : null;
        const departmentName = department?.label || "";
        const departmentId = department?.value || null;

        const employee = {
          branch: branch_id,
          supervisor: supers.length ? supers : undefined,
          department: departmentName || undefined,
          isLineManager: newObject.isLineManager
            ? newObject.isLineManager === "1"
            : null,
          personalInfo: {
            name: {
              first: newObject.personalInfo_name_first,
              last: newObject.personalInfo_name_last,
              middle: newObject.personalInfo_name_middle,
            },
            address: createNested(
              "personalInfo_address",
              ["apartment", "street", "city", "state", "zip"],
              newObject
            ),
            phone: createNested(
              "personalInfo_phone",
              ["home", "alternate"],
              newObject
            ),
            email: newObject.personalInfo_email,
            birth: formatDate(newObject.personalInfo_birth),
            isAdmin: newObject.admin ? newObject.admin === "1" : null,
            isSuperAdmin: newObject.admin ? newObject.admin === "1" : null,
            grantAccess: true,
            govtId: newObject.personalInfo_govtId,
            nationality: newObject.nationality,
            BVN: newObject.BVN,
            taxID: newObject.taxID,
            gender:
              newObject.personalInfo_gender?.charAt(0) === "M"
                ? "Male"
                : newObject.personalInfo_gender?.charAt(0) === "F"
                ? "Female"
                : undefined,
            martial: newObject.personalInfo_martial,
            govtType: newObject.personalInfo_govtType,
            spouse: createNested(
              "personalInfo_spouse",
              ["name", "employer", "phone"],
              newObject
            ),
            prevEmployee: createNested(
              "personalInfo_prevEmployee",
              ["employee", "phone"],
              newObject
            ),
          },
          jobInfo: {
            title: newObject.jobInfo_title,
            category: newObject.category
              ? categoryMap.get(newObject.category)
              : null,
            employeeId: newObject.jobInfo_employeeId,
            changeableId: newObject.jobInfo_employeeId,
            grade: newObject.job_grade
              ? gradeMap.get(newObject.job_grade)
              : null,
            company_id: company_id || null,
            department: departmentId,
            email: newObject.jobInfo_email,
            workPhone: newObject.jobInfo_workPhone,
            cellPhone: newObject.jobInfo_cellPhone,
            startDate: formatDate(newObject.jobInfo_startDate),
            joinDate: formatDate(newObject.jobInfo_confirmDate),
            confirmDate: formatDate(newObject.jobInfo_confirmDate),
            salary: newObject.jobInfo_salary,
            status: newObject.jobInfo_confirmDate ? "Confirmed" : undefined,
            sfa_id: newObject.jobInfo_sfa_id,
            state: newObject.jobInfo_state,
          },
          emergencyInfo: {
            name: createNested(
              "emergencyInfo_name",
              ["first", "last", "middle"],
              newObject
            ),
            address: createNested(
              "emergencyInfo_address",
              ["apartment", "street", "city", "state", "zip"],
              newObject
            ),
            phone: createNested(
              "emergencyInfo_phone",
              ["primary", "secondary"],
              newObject
            ),
            relation: newObject.emergencyInfo_relation,
            guarantor: createNested(
              "emergencyInfo_guarantor",
              ["title", "name", "phone", "address"],
              newObject
            ),
            referee: createNested(
              "emergencyInfo_referee",
              ["title", "name", "phone", "address"],
              newObject
            ),
          },
          user: {
            name: {
              first: newObject.personalInfo_name_first,
              last: newObject.personalInfo_name_last,
              middle: newObject.personalInfo_name_middle,
            },
            email: newObject.jobInfo_email || null,
            password: newObject.jobInfo_password
              ? `${newObject.jobInfo_password}`
              : null,
            isAdmin: newObject.admin ? newObject.admin === "1" : null,
            isSuperAdmin: newObject.admin ? newObject.admin === "1" : null,
            company_id,
            department_id: departmentId,
            grantAccess: newObject.admin ? newObject.admin === "1" : null,
            branch: branch_id,
            workEmail: newObject.jobInfo_email,
            gender:
              newObject.personalInfo_gender?.charAt(0) === "M"
                ? "Male"
                : newObject.personalInfo_gender?.charAt(0) === "F"
                ? "Female"
                : undefined,
            Staff_ID: newObject.jobInfo_employeeId,
          },
          bankDetails: createNested(
            "bankDetails",
            [
              "bankName",
              "accountNumber",
              "accountType",
              "accountOfficer",
              "bankCode",
              "pensionNumber",
              "pensionAdmin",
            ],
            newObject
          ),
        };

        return deepClean(employee);
      })
      .filter(Boolean);
  };

  async function handleFile({ eventData: e, addNew }) {
    setIsLoading(true);

    const file = e?.target?.files[0];

    if (file) {
      const reader = new FileReader();

      reader.onload = async function (e) {
        try {
          const workbook = read(e.target.result, {
            cellDates: true,
            dateNF: "yyyy-MM-dd HH:mm:ss.SSS",
          });

          let rows = utils.sheet_to_json(
            workbook.Sheets[workbook.SheetNames[0]],
            {
              defval: "",
              dateNF: "yyyy-MM-dd HH:mm:ss.SSS",
            }
          );

          const departments = state.department;
          const jobGrades = state.grades;
          const categories = state.category;

          // Precompute lookup maps for faster access
          const deptMap = new Map(departments?.map((d) => [d.label, d]));
          const gradeMap = new Map(jobGrades?.map((g) => [g.label, g.value]));
          const categoryMap = new Map(
            categories?.map((c) => [c.label, c.value])
          );

          if (rows?.length > 0) {
            let CleanedEmployees = [];

            if (addNew) {
              CleanedEmployees = addNewEmployees({
                rows,
                deptMap,
                gradeMap,
                categoryMap,
              });
              // Employee Validation
              const missingFields = validateEmployeeFields(CleanedEmployees);
              setEmployeeErrors(missingFields);
            } else {
              CleanedEmployees = updateEmployees({
                rows,
                deptMap,
                gradeMap,
                categoryMap,
              });
            }

            // console.log(CleanedEmployees);

            setEmployees(CleanedEmployees);
          }
        } catch (err) {
          console.log(err);
        } finally {
          setIsLoading(false);
        }
      };

      reader.readAsArrayBuffer(file);
      e.target.value = "";
    }
  }

  const saveEmployees = async () => {
    try {
      if (employeeErrors.length > 0) {
        return toast.error("Please rectify the errors before you continue");
      }

      setIsLoading(true);
      const response = await fetchActionsUtil(
        `${URLS.backendEmployees}/multiple-add`,
        "POST",
        token,
        {
          employees,
        }
      );
      if (!response.success) {
        return toast.error(response?.message || "Saving Error!!!", {
          position: "top-right",
        });
      }
      refresh();
      toast.success("Added successfully", { position: "top-right" });
      handleClose();
    } catch (error) {
      toast.error(error?.message);

      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      size={employeeErrors.length > 0 ? "lg" : ""}
    >
      <Modal.Header closeButton>
        <Modal.Title>Import Employees</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          <div className="d-flex justify-content-center mt-3">
            <label className="btn btn-outline-primary mx-3 p-cursor">
              <ImportIcon />
              <span className="mx-2" style={{ color: "#2463AE" }}>
                New Employee
              </span>
              <input
                type="file"
                className="d-none"
                onChange={(e) => {
                  handleFile({
                    eventData: e,
                    addNew: true,
                  });
                }}
                accept=".xlsx"
              />
            </label>
            <label className="btn btn-outline-primary mx-3 p-cursor">
              <ImportIcon />
              <span className="mx-2" style={{ color: "#2463AE" }}>
                Employee Updates
              </span>
              <input
                type="file"
                className="d-none"
                onChange={(e) => {
                  handleFile({
                    eventData: e,
                    addNew: false,
                  });
                }}
                accept=".xlsx"
              />
            </label>
          </div>
        </div>
        {/*  */}
        <div className="my-3">
          {employees.length > 0 ? (
            <div className="d-flex justify-content-center">
              <div>
                <div>
                  <p>{employees.length} employees detected</p>
                </div>
                <div>
                  {employeeErrors.length !== employees.length ? (
                    <>
                      {employeeErrors.length > 0 ? (
                        <p className="text-danger">
                          {employeeErrors.length} errors detected
                        </p>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/*  */}
        {employeeErrors.length > 0 ? (
          <div className="my-4">
            <Row className="table-height">
              <Table responsive striped sticky className="table-custom">
                <thead className="position-sticky top-0">
                  <tr>
                    <th>NO</th>
                    <th>Employee name</th>
                    <th>Employee ID</th>
                    <th>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeErrors.map((el, i) => (
                    <tr>
                      <td>{i + 1}</td>
                      <td>{el?.employeeName}</td>
                      <td>{el?.employeeId}</td>
                      <td>
                        <span className="text-danger"> {el?.error}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Row>
          </div>
        ) : null}
      </Modal.Body>

      {employees.length > 0 ? (
        <>
          {employeeErrors.length > 0 ? null : (
            <div className="d-flex justify-content-between my-3 mx-4">
              <div>
                <button className="btn btn-secondary" onClick={handleClose}>
                  Cancel
                </button>
              </div>
              <div>
                <button className="btn btn-primary" onClick={saveEmployees}>
                  Save
                </button>
              </div>
            </div>
          )}
        </>
      ) : null}
      <ModalLoader show={isLoading} />
    </Modal>
  );
};
